import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { useAuth } from '../contexts/AuthContext';
import { SearchIcon, CloseIcon, TargetIcon } from './Icons';
import EventCard from './EventCard';
import '../styles/EventsPage.css';

interface Event {
  id: string;
  name: string;
  description: string;
  type: 'event' | 'workshop' | 'exhibition' | 'seminar' | 'competition' | 'hackathon' | 'conference' | 'other';
  image?: string;
  date: string;
  created_at: string;
  club_id?: string;
  club_name?: string;
  fee: number;
  created_by: string;
}

const EventsPage: React.FC = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'participants' | 'popularity'>('date');

  const API_URL = BACKEND_URL;

  const eventTypes = [
    { value: 'all', label: 'All Events' },
    { value: 'hackathon', label: 'Hackathons' },
    { value: 'conference', label: 'Conferences' },
    { value: 'workshop', label: 'Workshops' },
    { value: 'seminar', label: 'Seminars' },
    { value: 'competition', label: 'Competitions' },
  ];

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/events`, { headers });
      // Backend returns an array directly, not wrapped in an object
      const eventList = Array.isArray(response.data) ? response.data : response.data.data || [];
      setEvents(eventList);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    filterAndSortEvents();
  }, [events, searchQuery, selectedFilter, sortBy]);

  const filterAndSortEvents = () => {
    let filtered = [...events];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (event) =>
          event.name.toLowerCase().includes(query) ||
          event.description.toLowerCase().includes(query) ||
          (event.club_name?.toLowerCase().includes(query))
      );
    }

    // Filter by event type
    if (selectedFilter !== 'all') {
      filtered = filtered.filter((event) => event.type === selectedFilter);
    }

    // Sort events
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case 'participants':
          // Since we don't have participant count, sort by date as fallback
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case 'popularity':
          // Sort by fee (higher fee = more popular/premium events)
          return (b.fee || 0) - (a.fee || 0);
        default:
          return 0;
      }
    });

    setFilteredEvents(filtered);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedFilter('all');
    setSortBy('date');
  };

  return (
    <motion.div
      className="events-page-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="events-page-header">
        <h1>Discover Events</h1>
        <p>Join clubs and participate in amazing events happening around you</p>
      </div>

      {/* Filters Section */}
      <div className="events-page-filters">
        {/* Search Bar */}
        <div className="search-box">
          <span className="search-icon"><SearchIcon size={16} color="#00ffff" /></span>
          <input
            type="text"
            placeholder="Search events, clubs, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <CloseIcon size={12} />
            </button>
          )}
        </div>

        {/* Filter Controls */}
        <div className="filter-controls">
          <div className="filter-group">
            <label>Event Type:</label>
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="filter-select"
            >
              {eventTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Sort By:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="filter-select"
            >
              <option value="date">Upcoming First</option>
              <option value="participants">Most Participants</option>
              <option value="popularity">Most Popular</option>
            </select>
          </div>

          {(searchQuery || selectedFilter !== 'all' || sortBy !== 'date') && (
            <motion.button
              className="clear-filters-btn"
              onClick={clearFilters}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Clear Filters
            </motion.button>
          )}
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <p>
          {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
          {searchQuery && ` matching "${searchQuery}"`}
        </p>
      </div>

      {/* Events Grid */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading events...</p>
        </div>
      ) : filteredEvents.length > 0 ? (
        <motion.div
          className="events-page-grid"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <AnimatePresence>
            {filteredEvents.map((event, idx) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: idx * 0.05, duration: 0.3 }}
              >
                <EventCard event={event} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <motion.div
          className="no-events"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="no-events-icon"><TargetIcon size={48} color="#00ffff" /></div>
          <h2>No Events Found</h2>
          <p>
            {searchQuery
              ? `No events match "${searchQuery}". Try a different search term.`
              : 'No events available at the moment. Check back soon!'}
          </p>
          <motion.button
            className="try-again-btn"
            onClick={clearFilters}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Clear Filters & Try Again
          </motion.button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default EventsPage;
